import React, { useEffect, useState, useRef, lazy } from 'react';
import { AlertCircle, CheckCircle2, ShieldCheck, TrendingUp, FileText, Users, Globe, Award, ArrowRight, Briefcase, ChevronRight, Factory, HardHat, Stethoscope, Truck, Building2, Landmark, Shield, MessageCircle, Phone, Mail, MapPin, Clock } from 'lucide-react';
export default function LandingPage() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const problemsRef = useRef<HTMLElement>(null);
  const solutionsRef = useRef<HTMLElement>(null);
  const [problemsVisible, setProblemsVisible] = useState(false);
  const [solutionsVisible, setSolutionsVisible] = useState(false);
  const videoUrl = 'https://cdn.magicpatterns.com/uploads/sKAXjqFp1wbCizSjgrrWTu/VIDEO-2025-02-07-07-12-27.mp4#t=0.1';
  const logoUrl = 'https://cdn.magicpatterns.com/uploads/i7oVmgzV6mzJdDFXdarJqH/image.png';
  useEffect(() => {
    const timer = setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.play().catch(error => {
          console.log('El navegador bloqueó el autoplay:', error);
        });
      }
    }, 4000);
    // Observer for Problems section
    const problemsObserver = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        setProblemsVisible(true);
        problemsObserver.disconnect(); // Only animate once
      }
    }, {
      threshold: 0.2
    });
    if (problemsRef.current) {
      problemsObserver.observe(problemsRef.current);
    }
    // Observer for Solutions section
    const solutionsObserver = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        setSolutionsVisible(true);
        solutionsObserver.disconnect(); // Only animate once
      }
    }, {
      threshold: 0.3
    });
    if (solutionsRef.current) {
      solutionsObserver.observe(solutionsRef.current);
    }
    return () => {
      clearTimeout(timer);
      problemsObserver.disconnect();
      solutionsObserver.disconnect();
    };
  }, []);
  return <div className="min-h-screen bg-white text-slate-800 font-sans selection:bg-red-100 selection:text-red-900" data-id="element-1">
      {/* NAVBAR PREMIUM */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-brand-dark/95 backdrop-blur-md border-b border-white/10 transition-all duration-300" data-id="element-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3">
          <div className="flex justify-between items-center h-24" data-id="element-4">
            <div className="flex-shrink-0 flex items-center" data-id="element-5">
              <img src={logoUrl} alt="Standard Consulting Logo" className="h-12 w-auto mix-blend-screen" data-id="element-6" />
            </div>
            <div className="hidden md:flex items-center space-x-8" data-id="element-7">
              <a href="#servicios" className="text-sm text-slate-300 hover:text-white font-medium tracking-wide transition-colors" data-id="element-8">
                SERVICIOS
              </a>
              <a href="#nosotros" className="text-sm text-slate-300 hover:text-white font-medium tracking-wide transition-colors" data-id="element-9">
                NOSOTROS
              </a>
              <a href="#contacto" className="text-sm text-slate-300 hover:text-white font-medium tracking-wide transition-colors" data-id="element-10">
                CONTACTO
              </a>
              <a href="#contacto" className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-white font-bold py-3 px-8 rounded-full text-sm tracking-wide transition-all shadow-[0_0_15px_rgba(239,68,68,0.5)] hover:shadow-[0_0_25px_rgba(239,68,68,0.8)] transform hover:-translate-y-0.5" data-id="element-11">
                DIAGNÓSTICO GRATUITO
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* 1. HERO SECTION PREMIUM */}
      <section className="relative pt-40 pb-24 px-4 sm:px-6 lg:px-8 text-center overflow-hidden bg-[#0B0F19]" data-id="element-12">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F19] via-[#111827] to-[#0B0F19] z-0" data-id="element-13"></div>

        {/* Subtle Finance Grid Texture (Increased opacity for more glow) */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff15_1px,transparent_1px),linear-gradient(to_bottom,#ffffff15_1px,transparent_1px)] bg-[size:40px_40px] z-0" data-id="element-14"></div>

        {/* Red Spotlight Behind Title */}
        <div className="absolute top-[20%] left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-red-600/20 via-red-900/5 to-transparent blur-[80px] pointer-events-none z-0" data-id="element-15"></div>

        {/* Bottom fade out to match next section */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-50 to-transparent z-0" data-id="element-16"></div>

        <div className="relative z-10 max-w-5xl mx-auto" data-id="element-17">
          <div className="inline-flex items-center space-x-2 mb-8 border border-white/10 bg-white/5 px-4 py-1.5 rounded-full backdrop-blur-sm shadow-lg" data-id="element-18">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse shadow-[0_0_8px_rgba(239,68,68,0.8)]" data-id="element-19"></span>
            <span className="text-xs font-medium tracking-widest uppercase text-slate-300" data-id="element-20">
              Firma de Auditoría y Consultoría
            </span>
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-medium leading-tight mb-8 text-white drop-shadow-lg" data-id="element-21">
            Transformamos la información financiera en{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-red-600 italic drop-shadow-none" data-id="element-22">
              confianza, transparencia y decisiones estratégicas.
            </span>
          </h1>

          <div className="relative w-full max-w-5xl mx-auto mb-12 rounded-lg overflow-hidden shadow-[0_0_40px_rgba(220,38,38,0.15)] border border-white/20 bg-slate-900 aspect-video group ring-1 ring-white/10 p-1 bg-gradient-to-b from-slate-800 to-slate-900" data-id="element-23">
            <div className="absolute inset-0 border border-white/5 z-20 pointer-events-none rounded-lg" data-id="element-24"></div>
            <video ref={videoRef} src={videoUrl} controls playsInline preload="metadata" className="w-full h-full object-cover relative z-10 rounded-md shadow-inner" data-id="element-25">
              Tu navegador no soporta el elemento de video.
            </video>
          </div>

          <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto mb-12 font-light leading-relaxed drop-shadow-md" data-id="element-26">
            Acompañamos a su empresa con revisoría fiscal, auditoría y
            consultoría bajo estándares internacionales (NIIF y NIAS),
            fortaleciendo su gobierno corporativo y generando valor sostenible.
          </p>

          <a href="#contacto" className="inline-flex bg-gradient-to-r from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-white font-bold py-4 px-10 rounded-full text-lg transition-all duration-300 shadow-[0_0_25px_rgba(239,68,68,0.4)] hover:shadow-[0_0_40px_rgba(239,68,68,0.7)] items-center justify-center mx-auto group tracking-wide transform hover:-translate-y-1" data-id="element-27">
            SOLICITAR DIAGNÓSTICO GRATUITO
            <ArrowRight className="ml-3 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-28" />
          </a>
        </div>
      </section>

      {/* 2. SECCIÓN PROBLEMAS (DOLOR) */}
      <section ref={problemsRef} className="py-24 bg-slate-50 px-4 sm:px-6 lg:px-8 relative overflow-hidden" data-id="element-29">
        {/* Subtle Light Grid Texture */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#00000006_1px,transparent_1px),linear-gradient(to_bottom,#00000006_1px,transparent_1px)] bg-[size:40px_40px] z-0 pointer-events-none" data-id="element-30"></div>

        {/* Bottom fade out to match next section */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent z-0" data-id="element-31"></div>

        <div className="max-w-5xl mx-auto relative z-10" data-id="element-32">
          <div className={`text-center mb-16 transition-all duration-1000 transform ${problemsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} data-id="element-33">
            <h2 className="text-3xl md:text-5xl font-serif font-medium text-slate-900 mb-6" data-id="element-34">
              ¿Te identificas con alguna de estas situaciones?
            </h2>
            <p className="text-slate-500 text-lg font-light tracking-wide" data-id="element-35">
              LOS DESCUIDOS FINANCIEROS HOY, SON LAS CRISIS DE MAÑANA.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-8" data-id="element-36">
            {['¿Cuenta con información financiera confiable y oportuna para la toma de decisiones?', '¿Su organización refleja altos estándares de transparencia y gobierno corporativo?', '¿Está aprovechando la auditoría como una herramienta estratégica y no solo de cumplimiento?', '¿Sus procesos financieros están alineados con estándares internacionales?', '¿Tiene claridad sobre los riesgos y oportunidades de su negocio?'].map((problem, idx, arr) => <div key={idx} className={`bg-white p-8 rounded-sm shadow-sm border border-slate-100 border-t-4 border-t-red-600 flex items-start space-x-5 hover:shadow-md transition-all duration-700 transform ${idx === arr.length - 1 && arr.length % 2 !== 0 ? 'sm:col-span-2 sm:max-w-2xl sm:mx-auto w-full' : ''} ${problemsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{
            transitionDelay: `${idx * 150}ms`
          }} data-id="element-37">
                <div className="mt-1" data-id="element-38">
                  <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" data-id="element-39" />
                </div>
                <p className="text-slate-700 font-medium text-lg leading-relaxed" data-id="element-40">
                  {problem}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 3. SECCIÓN SOLUCIÓN */}
      <section ref={solutionsRef} className="py-24 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden" data-id="element-41">
        {/* Subtle Light Grid Texture */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#00000004_1px,transparent_1px),linear-gradient(to_bottom,#00000004_1px,transparent_1px)] bg-[size:40px_40px] z-0 pointer-events-none" data-id="element-42"></div>

        {/* Bottom fade out to match next section */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0B0F19] to-transparent z-0" data-id="element-43"></div>

        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-16 relative z-10" data-id="element-44">
          <div className={`lg:w-1/2 transition-all duration-1000 transform ${solutionsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`} data-id="element-45">
            <div className="w-12 h-1 bg-red-600 mb-8" data-id="element-46"></div>
            <h2 className="text-3xl md:text-5xl font-serif font-medium text-slate-900 mb-8 leading-tight" data-id="element-47">
              Te ayudamos a tomar el control total de tu empresa
            </h2>
            <p className="text-lg text-slate-600 mb-10 font-light leading-relaxed" data-id="element-48">
              Con información clara, cumplimiento legal estricto y estrategias
              financieras inteligentes, transformamos la incertidumbre en
              seguridad, rentabilidad y crecimiento sostenido.
            </p>
            <ul className="space-y-6" data-id="element-49">
              {['Revisoría fiscal independiente que fortalece la transparencia, el gobierno corporativo y la confianza de inversionistas y stakeholders', 'Cumplimiento normativo integral y prevención de riesgos', 'Optimización tributaria dentro del marco legal', 'Protección patrimonial con visión de largo plazo', 'Información financiera confiable para la toma de decisiones estratégicas'].map((solution, idx) => <li key={idx} className={`flex items-start space-x-4 text-slate-800 font-medium transition-all duration-700 transform ${solutionsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`} style={{
              transitionDelay: `${400 + idx * 150}ms`
            }} data-id="element-50">
                  <CheckCircle2 className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" data-id="element-51" />
                  <span className="text-lg leading-relaxed" data-id="element-52">{solution}</span>
                </li>)}
            </ul>
          </div>
          <div className={`lg:w-1/2 w-full relative transition-all duration-1000 delay-300 transform ${solutionsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-53">
            {/* Premium image treatment with offset border */}
            <div className="absolute inset-0 border-2 border-red-600 translate-x-4 translate-y-4 rounded-sm hidden md:block" data-id="element-54"></div>
            <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Equipo de consultoría trabajando" loading="lazy" className="relative z-10 rounded-sm shadow-2xl w-full object-cover aspect-[4/3] grayscale-[20%]" data-id="element-55" />
          </div>
        </div>
      </section>

      {/* 4. SERVICIOS */}
      <section id="servicios" className="py-24 bg-brand-dark text-white px-4 sm:px-6 lg:px-8 relative overflow-hidden" data-id="element-56">
        {/* Subtle Dark Grid Texture */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:40px_40px] z-0 pointer-events-none" data-id="element-57"></div>

        {/* Top fade from previous section */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[#0B0F19] to-transparent z-0" data-id="element-58"></div>

        {/* Bottom fade out to match next section */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-50 to-transparent z-0" data-id="element-59"></div>

        <div className="max-w-7xl mx-auto relative z-10" data-id="element-60">
          <div className="text-center mb-20" data-id="element-61">
            <h2 className="text-3xl md:text-5xl font-serif font-medium mb-6" data-id="element-62">
              Nuestros Servicios Especializados
            </h2>
            <p className="text-red-400 text-sm md:text-base font-semibold tracking-widest uppercase" data-id="element-63">
              Soluciones diseñadas para proteger, optimizar y hacer crecer tu
              empresa
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mb-20" data-id="element-64">
            {/* Servicio 1 */}
            <div className="bg-slate-900/50 border border-white/10 p-10 rounded-sm hover:border-red-500/50 transition-colors group" data-id="element-65">
              <ShieldCheck className="w-10 h-10 text-red-500 mb-8 group-hover:scale-110 transition-transform" data-id="element-66" />
              <h3 className="text-2xl font-serif font-medium mb-4" data-id="element-67">
                Auditoría y Revisoría Fiscal
              </h3>
              <p className="text-slate-400 mb-8 font-light leading-relaxed" data-id="element-68">
                Garantizamos la transparencia y cumplimiento legal de tu
                empresa, detectando riesgos antes de que se conviertan en
                sanciones.
              </p>
              <ul className="text-sm text-slate-300 space-y-4" data-id="element-69">
                <li className="flex items-start" data-id="element-70">
                  <ChevronRight className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-71" />
                  <span data-id="element-72">Detección temprana de riesgos financieros</span>
                </li>
                <li className="flex items-start" data-id="element-73">
                  <ChevronRight className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-74" />
                  <span data-id="element-75">Cumplimiento con normativas DIAN</span>
                </li>
                <li className="flex items-start" data-id="element-76">
                  <ChevronRight className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-77" />
                  <span data-id="element-78">Informes claros para toma de decisiones</span>
                </li>
                <li className="flex items-start" data-id="element-79">
                  <ChevronRight className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-80" />
                  <span data-id="element-81">Prevención de sanciones y multas</span>
                </li>
              </ul>
            </div>

            {/* Servicio 2 */}
            <div className="bg-slate-900/50 border border-white/10 p-10 rounded-sm hover:border-red-500/50 transition-colors group" data-id="element-82">
              <TrendingUp className="w-10 h-10 text-red-500 mb-8 group-hover:scale-110 transition-transform" data-id="element-83" />
              <h3 className="text-2xl font-serif font-medium mb-4" data-id="element-84">
                Asesoría Tributaria y Financiera
              </h3>
              <p className="text-slate-400 mb-8 font-light leading-relaxed" data-id="element-85">
                Diseñamos estrategias legales para optimizar tu carga tributaria
                y mejorar la rentabilidad de tu negocio.
              </p>
              <ul className="text-sm text-slate-300 space-y-4" data-id="element-86">
                <li className="flex items-start" data-id="element-87">
                  <ChevronRight className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-88" />
                  <span data-id="element-89">Planeación tributaria inteligente</span>
                </li>
                <li className="flex items-start" data-id="element-90">
                  <ChevronRight className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-91" />
                  <span data-id="element-92">Reducción legal de impuestos</span>
                </li>
                <li className="flex items-start" data-id="element-93">
                  <ChevronRight className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-94" />
                  <span data-id="element-95">Análisis financiero estratégico</span>
                </li>
                <li className="flex items-start" data-id="element-96">
                  <ChevronRight className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-97" />
                  <span data-id="element-98">Acompañamiento en decisiones clave</span>
                </li>
              </ul>
            </div>

            {/* Servicio 3 */}
            <div className="bg-slate-900/50 border border-white/10 p-10 rounded-sm hover:border-red-500/50 transition-colors group" data-id="element-99">
              <FileText className="w-10 h-10 text-red-500 mb-8 group-hover:scale-110 transition-transform" data-id="element-100" />
              <h3 className="text-2xl font-serif font-medium mb-4" data-id="element-101">
                Outsourcing Contable y Administrativo
              </h3>
              <p className="text-slate-400 mb-8 font-light leading-relaxed" data-id="element-102">
                Nos encargamos de tu contabilidad para que tú te enfoques en
                hacer crecer tu empresa.
              </p>
              <ul className="text-sm text-slate-300 space-y-4" data-id="element-103">
                <li className="flex items-start" data-id="element-104">
                  <ChevronRight className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-105" />
                  <span data-id="element-106">Contabilidad al día y sin errores</span>
                </li>
                <li className="flex items-start" data-id="element-107">
                  <ChevronRight className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-108" />
                  <span data-id="element-109">Reportes financieros claros</span>
                </li>
                <li className="flex items-start" data-id="element-110">
                  <ChevronRight className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-111" />
                  <span data-id="element-112">Gestión de nómina y procesos administrativos</span>
                </li>
                <li className="flex items-start" data-id="element-113">
                  <ChevronRight className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-114" />
                  <span data-id="element-115">Optimización operativa</span>
                </li>
              </ul>
            </div>
          </div>

          {/* OTRAS SOLUCIONES ESPECIALIZADAS */}
          <div className="border-t border-white/10 pt-20" data-id="element-116">
            <h3 className="text-xl font-serif font-medium text-center mb-12 text-white" data-id="element-117">
              Otras Soluciones Especializadas
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-118">
              {[{
              title: 'Auditoría Financiera',
              desc: 'Evaluamos la salud económica de tu empresa con precisión'
            }, {
              title: 'Auditoría de Gestión',
              desc: 'Medimos la eficiencia y eficacia de tus operaciones'
            }, {
              title: 'Auditoría de Sistemas',
              desc: 'Protegemos tu seguridad de la información'
            }, {
              title: 'Auditoría en Salud',
              desc: 'Cumplimiento normativo para el sector salud'
            }, {
              title: 'Protección Patrimonial',
              desc: 'Estrategias legales para blindar tus activos'
            }, {
              title: 'Avalúo de Activos',
              desc: 'Valoración precisa y certificada de tus bienes'
            }, {
              title: 'Valoración de Empresas',
              desc: 'Determinamos el valor real de tu negocio'
            }, {
              title: 'Asesoría Aduanera',
              desc: 'Optimización en operaciones de comercio exterior'
            }].map((sol, idx) => <div key={idx} className="bg-transparent border border-white/5 p-6 rounded-sm hover:bg-white/5 transition-colors group" data-id="element-119">
                  <h4 className="font-bold text-white mb-2 text-sm uppercase tracking-wider group-hover:text-red-400 transition-colors" data-id="element-120">
                    {sol.title}
                  </h4>
                  <p className="text-slate-400 text-sm font-light leading-relaxed" data-id="element-121">
                    {sol.desc}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* 5. SOBRE NOSOTROS & EQUIPO */}
      <section id="nosotros" className="py-24 bg-slate-50 px-4 sm:px-6 lg:px-8 relative overflow-hidden" data-id="element-122">
        {/* Subtle Light Grid Texture */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#00000006_1px,transparent_1px),linear-gradient(to_bottom,#00000006_1px,transparent_1px)] bg-[size:40px_40px] z-0 pointer-events-none" data-id="element-123"></div>

        {/* Top fade from previous section */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-slate-50 to-transparent z-0" data-id="element-124"></div>

        {/* Bottom fade out to match next section */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent z-0" data-id="element-125"></div>

        <div className="max-w-7xl mx-auto relative z-10" data-id="element-126">
          {/* 5.1 Titular y Descripción */}
          <div className="text-center max-w-4xl mx-auto mb-20" data-id="element-127">
            <div className="inline-block border border-red-200 bg-red-50 text-red-600 font-semibold px-4 py-1.5 rounded-full text-xs mb-6 uppercase tracking-widest" data-id="element-128">
              Sobre Nosotros
            </div>
            <h2 className="text-3xl md:text-5xl font-serif font-medium text-slate-900 mb-8 leading-tight" data-id="element-129">
              Más que consultores, somos aliados estratégicos de tu empresa
            </h2>
            <p className="text-lg text-slate-600 font-light leading-relaxed" data-id="element-130">
              En Standard Consulting ayudamos a empresas a crecer con seguridad
              financiera, cumplimiento legal y decisiones estratégicas
              inteligentes. Nuestro compromiso es ofrecer soluciones claras,
              confiables y adaptadas a cada negocio.
            </p>
          </div>

          {/* 5.2 Bloque de Confianza */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-24" data-id="element-131">
            {[{
            icon: Globe,
            text: 'Experiencia en múltiples sectores económicos'
          }, {
            icon: Users,
            text: 'Equipo multidisciplinario altamente calificado'
          }, {
            icon: Award,
            text: 'Especialistas en NIIF y normativas internacionales'
          }, {
            icon: Shield,
            text: 'Enfoque en integridad, transparencia y resultados'
          }].map((item, idx) => <div key={idx} className="bg-white p-6 rounded-sm shadow-sm border border-slate-100 flex flex-col items-center text-center hover:shadow-md transition-shadow" data-id="element-132">
                <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mb-4 border border-slate-100" data-id="element-133">
                  <item.icon className="w-6 h-6 text-red-600" data-id="element-134" />
                </div>
                <p className="text-slate-700 font-medium text-sm leading-relaxed" data-id="element-135">
                  {item.text}
                </p>
              </div>)}
          </div>

          {/* 5.3 A quiénes ayudamos (Industrias) */}
          <div className="bg-brand-dark rounded-sm p-12 md:p-16 mb-24 relative overflow-hidden" data-id="element-136">
            <div className="absolute top-0 right-0 w-64 h-64 bg-red-600/10 blur-[80px] rounded-full pointer-events-none" data-id="element-137"></div>
            <div className="relative z-10 flex flex-col lg:flex-row items-center gap-12" data-id="element-138">
              <div className="lg:w-1/3" data-id="element-139">
                <h3 className="text-2xl md:text-3xl font-serif font-medium text-white mb-4" data-id="element-140">
                  A quiénes ayudamos
                </h3>
                <div className="w-12 h-1 bg-red-600 mb-6" data-id="element-141"></div>
                <p className="text-slate-400 font-light leading-relaxed" data-id="element-142">
                  Trabajamos con empresas de diferentes sectores económicos,
                  entendiendo las necesidades específicas de cada industria.
                </p>
              </div>
              <div className="lg:w-2/3 grid grid-cols-2 sm:grid-cols-3 gap-6 w-full" data-id="element-143">
                {[{
                icon: Factory,
                name: 'Manufactura'
              }, {
                icon: HardHat,
                name: 'Construcción'
              }, {
                icon: Stethoscope,
                name: 'Salud'
              }, {
                icon: Truck,
                name: 'Transporte'
              }, {
                icon: Building2,
                name: 'Servicios'
              }, {
                icon: Landmark,
                name: 'Sector Financiero'
              }].map((industry, idx) => <div key={idx} className="flex items-center space-x-3 bg-white/5 border border-white/10 p-4 rounded-sm hover:bg-white/10 transition-colors" data-id="element-144">
                    <industry.icon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-145" />
                    <span className="text-slate-200 text-sm font-medium" data-id="element-146">
                      {industry.name}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>

          {/* 5.4 Equipo */}
          <div className="mb-32" data-id="element-147">
            <div className="text-center mb-20" data-id="element-148">
              <h3 className="text-3xl md:text-5xl font-serif font-medium text-slate-900 mb-8" data-id="element-149">
                Equipo Especializado
              </h3>
              <div className="text-slate-500 font-light text-lg max-w-3xl mx-auto space-y-4 leading-relaxed" data-id="element-150">
                <p data-id="element-151">
                  Nuestro equipo está conformado por profesionales altamente
                  calificados en auditoría, revisoría fiscal y consultoría, con
                  sólida formación técnica y visión estratégica del negocio.
                </p>
                <p data-id="element-152">
                  Combinamos experiencia local con estándares internacionales
                  (NIIF y NIAS), aportando análisis rigurosos, independencia
                  profesional y un enfoque orientado a la generación de valor.
                </p>
                <p data-id="element-153">
                  Acompañamos a nuestros clientes más allá del cumplimiento,
                  brindando claridad, confianza y soporte en la toma de
                  decisiones clave.
                </p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-id="element-154">
              {[{
              name: 'Byron Montoya',
              role: 'Founder & CEO',
              desc: 'Asesora organizaciones en consultoría, auditoría y revisoría fiscal con enfoque estratégico y estándares internacionales (NIIF, NIAS, US GAAP). Aporta una visión integral en control, gobierno corporativo y toma de decisiones, con experiencia en firmas Big Four y diversos sectores economicos.',
              image: 'https://cdn.magicpatterns.com/uploads/4sFQTvyfcaqAYhrZyT8EYQ/image.png',
              alwaysColor: true
            }, {
              name: 'Mauricio Ceballos',
              role: 'Audit & Assurance | CFO',
              desc: 'Integra visión financiera y estratégica para fortalecer la toma de decisiones y la sostenibilidad empresarial. Cuenta con experiencia en auditoría y revisoría fiscal en diversos sectores, con enfoque en estándares internacionales y gestión financiera.',
              image: 'https://cdn.magicpatterns.com/uploads/qWnzBKUjjFG3KXGTg5s79G/image.png',
              alwaysColor: true
            }, {
              name: 'Sabi Briceño',
              role: 'Audit & Assurance - Consulting',
              desc: 'Especialista en auditoría con enfoque en sistemas de información y control interno. Apoya a las organizaciones en el fortalecimiento de sus procesos financieros y en la generación de información confiable.',
              image: 'https://cdn.magicpatterns.com/uploads/1Uej3QCqg8hW4jsTwC6Qgc/sabi_briceno.png',
              alwaysColor: true
            }, {
              name: 'Eliana Robledo',
              role: 'Audit & Assurance - Consulting',
              desc: 'Especialista en auditoría y revisoría fiscal, con experiencia en el sector real y entidades sin ánimo de lucro. Contribuye a la transparencia y cumplimiento mediante un enfoque técnico orientado a la confiabilidad de la información financiera.',
              image: 'https://cdn.magicpatterns.com/uploads/k1jfynKXyWiJBah32QcGi9/Eliana_robledo.png',
              alwaysColor: true
            }, {
              name: 'Daniela Gutiérrez',
              role: 'Audit & Assurance',
              desc: 'Acompaña organizaciones en procesos de auditoría y gobierno corporativo, aportando una visión estructurada y alineada con estándares internacionales. Cuenta con experiencia en firmas Big Four y conocimiento en derecho societario y tributación internacional.',
              image: 'https://cdn.magicpatterns.com/uploads/3SYxM8wzDueVhnxvAWeByU/daniel_gutierrez.png',
              alwaysColor: true
            }, {
              name: 'Daniel Serna',
              role: 'Consulting & Audit Manager',
              desc: 'Lidera procesos de auditoría y consultoría, apoyando a las organizaciones en la implementación de estándares internacionales y en la mejora de sus procesos financieros y de control.',
              image: 'https://cdn.magicpatterns.com/uploads/fxPBC4hedQAsGqGhnjVuWN/daniel_serna.png',
              alwaysColor: true
            }, {
              name: 'Alejandro González',
              role: 'Tax Manager',
              desc: 'Asesora en materia tributaria con un enfoque estratégico y legal, integrando su formación como contador y abogado. Cuenta con experiencia en liderazgo fiscal en el sector salud y en la gestión de impuestos corporativos.',
              image: 'https://cdn.magicpatterns.com/uploads/paWXzqbK15JoDCQX7PyFTC/alejandro.png',
              alwaysColor: true
            }, {
              name: 'Adriana Mesa',
              role: 'Manager T.I.',
              desc: 'Ingeniera de Sistemas con experiencia en procesos de revisoría fiscal en firmas Big Four, empresas del sector real y entidades sin ánimo de lucro. Especialista en la integración de tecnología y auditoría, aporta valor mediante la evaluación de controles de TI, gestión de riesgos tecnológicos y fortalecimiento de entornos digitales seguros.',
              image: 'https://cdn.magicpatterns.com/uploads/6XJXKnx5FNGGw8BHc6o2e3/adriana.png',
              alwaysColor: true
            }].map((member, idx) => <div key={idx} className="bg-white rounded-lg shadow-md shadow-slate-200/50 border border-slate-100 p-8 text-center hover:shadow-2xl hover:shadow-slate-200 transition-all duration-300 transform hover:-translate-y-1 group" data-id="element-155">
                  <div className="w-32 h-32 mx-auto rounded-full bg-slate-200 relative overflow-hidden mb-6 border-4 border-white shadow-lg group-hover:border-red-50 transition-colors" data-id="element-156">
                    {member.image ? <img src={member.image} alt={member.name} loading="lazy" className={`w-full h-full object-cover transition-all duration-500 scale-100 group-hover:scale-105 ${member.alwaysColor ? '' : 'grayscale group-hover:grayscale-0'}`} data-id="element-157" /> : member.bgPos ? <div className={`w-full h-full transition-all duration-500 scale-100 group-hover:scale-105 bg-no-repeat ${member.alwaysColor ? '' : 'grayscale group-hover:grayscale-0'}`} style={{
                  backgroundImage: `url('https://cdn.magicpatterns.com/uploads/eUNAGi7Ar5nEKfaRLVWXMs/image.png')`,
                  backgroundPosition: member.bgPos,
                  backgroundSize: member.bgSize
                }} data-id="element-158"></div> : <img src={`https://images.unsplash.com/photo-${1573496359142 + (member.placeholderIdx || 0)}-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80`} alt={member.name} loading="lazy" className="w-full h-full object-cover transition-all duration-500 scale-100 group-hover:scale-105 grayscale group-hover:grayscale-0" data-id="element-159" />}
                  </div>
                  <div data-id="element-160">
                    <h4 className="font-serif font-medium text-slate-900 text-xl mb-1 group-hover:text-red-600 transition-colors" data-id="element-161">
                      {member.name}
                    </h4>
                    <p className="text-red-600 text-xs font-bold uppercase tracking-wider mb-4" data-id="element-162">
                      {member.role}
                    </p>
                    <p className="text-slate-600 text-sm font-light leading-relaxed" data-id="element-163">
                      {member.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>

          {/* 5.5 Nuestro Enfoque (Diferencial) */}
          <div className="bg-slate-100 border border-slate-200 p-10 md:p-16 rounded-sm text-center max-w-4xl mx-auto" data-id="element-164">
            <ShieldCheck className="w-12 h-12 text-red-600 mx-auto mb-6" data-id="element-165" />
            <h3 className="text-2xl font-serif font-medium text-slate-900 mb-6" data-id="element-166">
              Nuestro enfoque
            </h3>
            <p className="text-lg md:text-xl text-slate-700 font-light leading-relaxed italic" data-id="element-167">
              "No solo auditamos tu empresa. La protegemos, optimizamos y la
              preparamos para crecer de forma sostenible, con estrategias
              financieras inteligentes y cumplimiento total."
            </p>
          </div>
        </div>
      </section>

      {/* 5.5 ALIADOS ESTRATÉGICOS (PRUEBA SOCIAL) */}
      <section className="py-24 bg-white px-4 sm:px-6 lg:px-8 relative overflow-hidden" data-id="element-168">
        {/* Subtle Light Grid Texture */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#00000004_1px,transparent_1px),linear-gradient(to_bottom,#00000004_1px,transparent_1px)] bg-[size:40px_40px] z-0 pointer-events-none" data-id="element-169"></div>

        {/* Bottom fade out to match next section */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-50 to-transparent z-0" data-id="element-170"></div>

        <div className="max-w-6xl mx-auto text-center relative z-10" data-id="element-171">
          <div className="mb-16" data-id="element-172">
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-slate-900 mb-6" data-id="element-173">
              Aliados estratégicos que respaldan nuestro trabajo
            </h2>
            <p className="text-lg text-slate-500 font-light max-w-3xl mx-auto leading-relaxed" data-id="element-174">
              Hemos trabajado con empresas de diferentes sectores, brindando
              soluciones confiables en auditoría, consultoría y asesoría
              financiera.
            </p>
          </div>

          <div className="mb-12" data-id="element-175">
            <img src="https://cdn.magicpatterns.com/uploads/pg5dWpPQKN2HawQvCHsvci/image.png" alt="Logos de empresas aliadas y clientes" loading="lazy" className="w-full max-w-4xl mx-auto rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 object-contain bg-white" data-id="element-176" />
          </div>

          <div className="max-w-3xl mx-auto" data-id="element-177">
            <p className="text-slate-600 font-medium mb-8" data-id="element-178">
              Trabajamos con empresas de múltiples sectores, generando
              confianza, cumplimiento y crecimiento sostenible para nuestros
              clientes.
            </p>

            <div className="pt-8 border-t border-slate-100" data-id="element-179">
              <p className="text-sm text-slate-400 uppercase tracking-widest mb-4 font-semibold" data-id="element-180">
                Experiencia en sectores como:
              </p>
              <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-3 text-slate-700 font-medium" data-id="element-181">
                <span data-id="element-182">Construcción</span>
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" data-id="element-183"></span>
                <span data-id="element-184">Salud</span>
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" data-id="element-185"></span>
                <span data-id="element-186">Transporte</span>
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" data-id="element-187"></span>
                <span data-id="element-188">Servicios</span>
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" data-id="element-189"></span>
                <span data-id="element-190">Sector financiero</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. PRUEBA SOCIAL (TESTIMONIOS) */}
      <section className="py-24 bg-slate-50 px-4 sm:px-6 lg:px-8 relative overflow-hidden" data-id="element-191">
        {/* Subtle Light Grid Texture */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#00000006_1px,transparent_1px),linear-gradient(to_bottom,#00000006_1px,transparent_1px)] bg-[size:40px_40px] z-0 pointer-events-none" data-id="element-192"></div>

        {/* Bottom fade out to match next section */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0B0F19] to-transparent z-0" data-id="element-193"></div>

        <div className="max-w-7xl mx-auto relative z-10" data-id="element-194">
          <div className="text-center mb-20" data-id="element-195">
            <h2 className="text-3xl md:text-5xl font-serif font-medium text-slate-900 mb-6" data-id="element-196">
              Resultados que hablan por nosotros
            </h2>
            <div className="w-12 h-1 bg-red-600 mx-auto" data-id="element-197"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-198">
            {[{
            name: 'Ricardo Rodriguez',
            role: 'CEO y Fundador',
            videoUrl: 'https://cdn.magicpatterns.com/uploads/xbpkgzr8uAcXBFgBh9XbpZ/RICARDO%20RODRIGUEZ%20(1).mp4#t=0.001'
          }, {
            name: 'Felipe Ceballos',
            role: 'Empresario y CEO de Grupo Empresarial',
            videoUrl: 'https://cdn.magicpatterns.com/uploads/fWihg2UEm6ZLbPBgaYomv6/FELIPE%20CEBALLOS%20(1).mp4#t=0.001'
          }, {
            name: 'Nickol Verdesoto',
            role: 'Directora Financiera (CFO)',
            videoUrl: 'https://cdn.magicpatterns.com/uploads/4YgWZFBdoUTNVbRQL7duPm/NICKOL%20VERDESOTO%20(1)%20(1).mp4#t=0.001'
          }, {
            name: 'Daniela Sanchez',
            role: 'Socia Inversionista',
            videoUrl: 'https://cdn.magicpatterns.com/uploads/cC6dAgMpGen7V8uquC4naS/DANIELA%20SANCHEZ%20(1).mp4#t=0.001'
          }].map((testimonial, idx) => <div key={idx} className="bg-white rounded-xl shadow-xl shadow-slate-200/40 border border-slate-100 overflow-hidden group" data-id="element-199">
                <div className="aspect-video bg-slate-900 relative" data-id="element-200">
                  {testimonial.videoUrl ? <video src={testimonial.videoUrl} controls playsInline preload="metadata" className="w-full h-full object-cover" data-id="element-201">
                      Tu navegador no soporta el elemento de video.
                    </video> : <div className="w-full h-full flex items-center justify-center bg-slate-800 text-slate-500" data-id="element-202">
                      <span className="font-medium" data-id="element-203">Video próximamente</span>
                    </div>}
                </div>
                <div className="p-6 border-t border-slate-100" data-id="element-204">
                  <p className="font-bold text-slate-900 tracking-wide text-lg" data-id="element-205">
                    {testimonial.name}
                  </p>
                  <p className="text-sm text-red-600 font-semibold uppercase tracking-wider" data-id="element-206">
                    {testimonial.role}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* 7. CONTACTO DIRECTO */}
      <section id="contacto" className="py-24 bg-brand-dark px-4 sm:px-6 lg:px-8 relative overflow-hidden" data-id="element-207">
        {/* Subtle Dark Grid Texture */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:40px_40px] z-0 pointer-events-none" data-id="element-208"></div>

        {/* Top fade from previous section */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[#0B0F19] to-transparent z-0" data-id="element-209"></div>

        {/* Decorative background element */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-red-600/5 blur-[120px] rounded-full pointer-events-none z-0" data-id="element-210"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-600/5 blur-[120px] rounded-full pointer-events-none z-0" data-id="element-211"></div>

        <div className="max-w-7xl mx-auto relative z-10" data-id="element-212">
          {/* 7.1 Titular y Subtítulo */}
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-213">
            <h2 className="text-4xl md:text-5xl font-serif font-medium text-white mb-6" data-id="element-214">
              Contáctanos y recibe tu diagnóstico financiero
            </h2>
            <p className="text-xl text-slate-400 font-light leading-relaxed" data-id="element-215">
              Nuestro equipo está listo para ayudarte a proteger y hacer crecer
              tu empresa.
            </p>
          </div>

          {/* 7.2 Botones de Acción Principales */}
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6 mb-20" data-id="element-216">
            <a href="https://wa.me/573146152758" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto flex items-center justify-center space-x-3 bg-[#25D366] hover:bg-[#1ebd5b] text-white font-bold py-5 px-8 rounded-full text-lg transition-all duration-300 shadow-[0_0_20px_rgba(37,211,102,0.3)] hover:shadow-[0_0_30px_rgba(37,211,102,0.5)] transform hover:-translate-y-1" data-id="element-217">
              <MessageCircle className="w-6 h-6" data-id="element-218" />
              <span data-id="element-219">Agendar por WhatsApp</span>
            </a>
            <a href="tel:+573146152758" className="w-full sm:w-auto flex items-center justify-center space-x-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-white font-bold py-5 px-8 rounded-full text-lg transition-all duration-300 shadow-[0_0_20px_rgba(239,68,68,0.3)] hover:shadow-[0_0_30px_rgba(239,68,68,0.5)] transform hover:-translate-y-1" data-id="element-220">
              <Phone className="w-6 h-6" data-id="element-221" />
              <span data-id="element-222">Llamar ahora</span>
            </a>
          </div>

          {/* 7.3 Información de Contacto Organizada */}
          <div className="grid lg:grid-cols-3 gap-8 mb-16" data-id="element-223">
            {/* Columna 1: Contacto Principal */}
            <div className="bg-slate-900/80 border border-white/10 rounded-sm p-8 backdrop-blur-sm" data-id="element-224">
              <h3 className="text-xl font-serif font-medium text-white mb-6 flex items-center" data-id="element-225">
                <Phone className="w-5 h-5 text-red-500 mr-3" data-id="element-226" />
                Líneas de Atención
              </h3>
              <div className="space-y-6" data-id="element-227">
                <div data-id="element-228">
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-2" data-id="element-229">
                    Colombia (PBX)
                  </p>
                  <p className="text-lg text-slate-300 font-medium" data-id="element-230">
                    (604) 591 5681
                  </p>
                  <p className="text-lg text-slate-300 font-medium" data-id="element-231">
                    (+57) 314 615 2758
                  </p>
                </div>
                <div className="pt-4 border-t border-white/5" data-id="element-232">
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-2" data-id="element-233">
                    Estados Unidos (Miami)
                  </p>
                  <p className="text-lg text-slate-300 font-medium" data-id="element-234">
                    +1 954 795 41 98
                  </p>
                </div>
                <div className="pt-4 border-t border-white/5" data-id="element-235">
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-2" data-id="element-236">
                    Correo Electrónico
                  </p>
                  <p className="text-sm text-slate-300 font-medium break-all" data-id="element-237">
                    mauricioceballos@standardconsulting.com.co
                  </p>
                </div>
              </div>
            </div>

            {/* Columna 2: Sedes Colombia */}
            <div className="bg-slate-900/80 border border-white/10 rounded-sm p-8 backdrop-blur-sm lg:col-span-2" data-id="element-238">
              <h3 className="text-xl font-serif font-medium text-white mb-6 flex items-center" data-id="element-239">
                <MapPin className="w-5 h-5 text-red-500 mr-3" data-id="element-240" />
                Nuestras Sedes
              </h3>
              <div className="grid sm:grid-cols-2 gap-8" data-id="element-241">
                <div data-id="element-242">
                  <h4 className="text-white font-medium mb-2" data-id="element-243">
                    Medellín (Principal)
                  </h4>
                  <p className="text-slate-400 text-sm font-light" data-id="element-244">
                    Calle 33 # 83-16 Int. 201
                  </p>
                </div>
                <div data-id="element-245">
                  <h4 className="text-white font-medium mb-2" data-id="element-246">Bogotá</h4>
                  <p className="text-slate-400 text-sm font-light" data-id="element-247">
                    Calle 93 # 11A-28 Oficina 601
                  </p>
                </div>
                <div data-id="element-248">
                  <h4 className="text-white font-medium mb-2" data-id="element-249">Barranquilla</h4>
                  <p className="text-slate-400 text-sm font-light" data-id="element-250">
                    Carrera 52 No. 69-96 Oficina 401
                  </p>
                </div>
                <div data-id="element-251">
                  <h4 className="text-white font-medium mb-2" data-id="element-252">Pereira</h4>
                  <p className="text-slate-400 text-sm font-light" data-id="element-253">
                    Carrera 25 # 86-71 Torre 1 Int. 301
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 7.4 Bloque de Confianza */}
          <div className="flex flex-col sm:flex-row items-center justify-center text-center sm:text-left space-y-4 sm:space-y-0 sm:space-x-4 bg-white/5 border border-white/10 rounded-sm p-6 max-w-3xl mx-auto" data-id="element-254">
            <Clock className="w-8 h-8 text-red-500 flex-shrink-0" data-id="element-255" />
            <p className="text-slate-300 font-light text-lg" data-id="element-256">
              <strong className="text-white font-medium" data-id="element-257">
                Respondemos en menos de 24 horas.
              </strong>{' '}
              Atención personalizada y estrictamente confidencial.
            </p>
          </div>
        </div>
      </section>

      {/* FOOTER PREMIUM */}
      <footer className="bg-[#05080f] py-16 text-center text-slate-500 text-sm border-t border-white/5" data-id="element-258">
        <div className="max-w-7xl mx-auto px-4 flex flex-col items-center" data-id="element-259">
          <img src={logoUrl} alt="Standard Consulting Logo" loading="lazy" className="h-10 w-auto mb-10 mix-blend-screen opacity-50 hover:opacity-100 transition-all grayscale hover:grayscale-0" data-id="element-260" />
          <div className="flex space-x-6 mb-8 text-xs tracking-widest uppercase" data-id="element-261">
            <a href="#" className="hover:text-white transition-colors" data-id="element-262">
              Aviso de Privacidad
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-263">
              Términos y Condiciones
            </a>
          </div>
          <p className="font-light" data-id="element-264">
            © {new Date().getFullYear()} Standard Consulting. Todos los
            derechos reservados.
          </p>
        </div>
      </footer>

      {/* FLOATING WHATSAPP BUTTON */}
      <a href="https://wa.me/573146152758" target="_blank" rel="noopener noreferrer" aria-label="Contactar por WhatsApp" className="fixed bottom-6 left-6 z-50 group flex items-center justify-center w-14 h-14 rounded-full bg-[#25D366] shadow-lg shadow-green-500/40 hover:scale-110 hover:shadow-green-500/60 transition-all duration-300" data-id="element-265">
        <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-30" data-id="element-266"></span>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="w-7 h-7 relative z-10" data-id="element-267">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" data-id="element-268" />
        </svg>
      </a>
    </div>;
}